import React, { useState } from 'react';
import axios from 'axios';
import { X, DollarSign, Loader2 } from 'lucide-react';

function PriceEditModal({ productId, productTitle, currentPrice, onClose, onPriceUpdated }) {
  const [price, setPrice] = useState(currentPrice || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const baseUrl = process.env.REACT_APP_API_URL || '';

  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPrice(value);
      setError('');
    }
  };

  const handleSave = async () => {
    const priceNum = parseFloat(price);
    
    if (!price || isNaN(priceNum)) {
      setError('Por favor ingresa un precio válido');
      return;
    }

    if (priceNum < 0) {
      setError('El precio no puede ser negativo');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const { data } = await axios.patch(
        `${baseUrl}/api/products/${productId}`,
        { price: priceNum }
      );

      onPriceUpdated(productId, data.price);
    } catch (error) {
      console.error('Error updating price:', error);
      setError(error.response?.data?.error || 'Error al actualizar el precio');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !saving) {
      handleSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 font-montserrat">
                Editar Precio
              </h2>
              <p className="text-sm text-slate-600 font-montserrat truncate max-w-xs">
                {productTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="mb-2">
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Precio (S/)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-montserrat">
                S/
              </span>
              <input
                type="text"
                value={price}
                onChange={handlePriceChange}
                onKeyPress={handleKeyPress}
                placeholder="0.00"
                disabled={saving}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-lg font-semibold font-montserrat disabled:opacity-50 disabled:bg-slate-50"
                autoFocus
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 font-montserrat">{error}</p>
            )}
          </div>

          {currentPrice && (
            <p className="text-xs text-slate-500 font-montserrat">
              Precio actual: S/ {parseFloat(currentPrice).toFixed(2)}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl font-semibold font-montserrat hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !price}
            className="flex-1 px-4 py-2.5 bg-brand-primary text-white rounded-xl font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PriceEditModal;